import { isHTMLEmpty } from '@proton/mail/helpers/dom';
import { containsHTMLTag, replaceLineBreaks } from '@proton/mail/helpers/string';
import type { MessageState } from '@proton/mail/store/messages/messagesTypes';
import { sanitizeMessage } from '@proton/sanitize/purify';
import { hasBit } from '@proton/shared/lib/helpers/bitset';
import { parseStringToDOM } from '@proton/shared/lib/helpers/dom';
import type { MailSettings } from '@proton/shared/lib/interfaces/MailSettings';
import type { UserSettings } from '@proton/shared/lib/interfaces/UserSettings';
import { PM_SIGNATURE } from '@proton/shared/lib/mail/mailSettings';
import { isPlainText } from '@proton/shared/lib/mail/messages';
import { getProtonMailSignature } from '@proton/shared/lib/mail/signature';
import isTruthy from '@proton/utils/isTruthy';

import { dedentTpl } from '../dedent';
import { exportPlainText, getPlainTextContent } from './messageContentPlainText';
import { CLASSNAME_BLOCKQUOTE } from './messageDraftConstants';

export const CLASSNAME_SIGNATURE_CONTAINER = 'protonmail_signature_block';
export const CLASSNAME_SIGNATURE_USER = 'protonmail_signature_block-user';
const CLASSNAME_SIGNATURE_PROTON = 'protonmail_signature_block-proton';
export const CLASSNAME_SIGNATURE_EMPTY = 'protonmail_signature_block-empty';

// Cache signatures to make composing and signature change faster
const signatureCache = new Map<string, string>();

export const exportPlainTextSignature = (htmlSignature: string): string => {
    if (signatureCache.has(htmlSignature)) {
        return signatureCache.get(htmlSignature)!;
    }

    const plaintext = exportPlainText(htmlSignature);

    signatureCache.set(htmlSignature, plaintext);
    return plaintext;
};

/**
 * Preformat the protonMail signature
 */
const getProtonSignature = (mailSettings: Partial<MailSettings> = {}, userSettings: Partial<UserSettings> = {}) => {
    const enabled = hasBit(mailSettings?.PMSignature, PM_SIGNATURE.ENABLED);
    const locked = hasBit(mailSettings?.PMSignature, PM_SIGNATURE.LOCKED);
    const enabledAndLocked = enabled && locked;

    // Users who can toggle the signature and have enabled it should see it
    if ((enabled && !locked) || enabledAndLocked) {
        return getProtonMailSignature(
            !!mailSettings.PMSignatureReferralLink,
            userSettings.Referral?.Link,
            mailSettings.PMSignatureContent
        );
    }

    // All other users don't see it
    return '';
};
/**
 * Generate a space tag, it can be hidden from the UX via a className
 */
const createSpace = (style?: string, className?: string) => {
    const tagOpen = [
        'div',
        style === undefined ? undefined : `style="${style}"`,
        className === undefined ? undefined : `class="${className}"`,
    ]
        .filter(isTruthy)
        .join(' ');
    return `<${tagOpen}><br /></div>`;
};

/**
 * Generate spaces for the signature
 *     No signature: 1 space
 *     addressSignature: 2 spaces + addressSignature
 *     protonSignature: 2 spaces + protonSignature
 *     user + proton signature: 2 spaces + addressSignature + 1 space + protonSignature
 */
const getSpaces = (signature: string, protonSignature: string, fontStyle: string | undefined) => {
    const isUserSignatureEmpty = isHTMLEmpty(signature);
    const isEmptySignature = isUserSignatureEmpty && !protonSignature;
    return {
        start: isEmptySignature ? createSpace(fontStyle) : createSpace(fontStyle) + createSpace(fontStyle),
        between: !isUserSignatureEmpty && protonSignature ? createSpace(fontStyle) : '',
    };
};

/**
 * Generate a map of classNames used for the signature template
 */
const getClassNamesSignature = (signature: string, protonSignature: string) => {
    const isUserEmpty = isHTMLEmpty(signature);
    const isProtonEmpty = !protonSignature;
    return {
        userClass: isUserEmpty ? CLASSNAME_SIGNATURE_EMPTY : '',
        protonClass: isProtonEmpty ? CLASSNAME_SIGNATURE_EMPTY : '',
        containerClass: isUserEmpty && isProtonEmpty ? CLASSNAME_SIGNATURE_EMPTY : '',
    };
};

/**
 * Generate the template for a signature and clean it
 */
export const templateBuilder = (
    signature = '',
    mailSettings: Partial<MailSettings> | undefined = {},
    userSettings: Partial<UserSettings> | undefined = {},
    fontStyle: string | undefined,
    noSpace = false
) => {
    const protonSignature = getProtonSignature(mailSettings, userSettings);
    const { userClass, protonClass, containerClass } = getClassNamesSignature(signature, protonSignature);
    const space = getSpaces(signature, protonSignature, fontStyle);

    const formattedSignature: string = (() => {
        const isEmptyContent = isHTMLEmpty(signature);
        if (isEmptyContent) {
            return '';
        }

        const signatureContainsHTML = containsHTMLTag(signature);

        if (signatureContainsHTML) {
            return signature;
        }

        return replaceLineBreaks(signature);
    })();

    const defaultStyle = fontStyle === undefined ? '' : `style="${fontStyle}" `;
    const template = dedentTpl`
        <div ${defaultStyle}class="${CLASSNAME_SIGNATURE_CONTAINER} ${containerClass}">
            <div class="${CLASSNAME_SIGNATURE_USER} ${userClass}">
                ${formattedSignature}
            </div>
            ${space.between}
            <div class="${CLASSNAME_SIGNATURE_PROTON} ${protonClass}">
                ${replaceLineBreaks(protonSignature)}
            </div>
        </div>
    `;

    if (!noSpace) {
        return `${space.start}${sanitizeMessage(template)}`;
    }

    return sanitizeMessage(template);
};

/**
 * Insert Signatures before the message
 *     - Always append a container signature with both user's and proton's
 *     - Theses signature can be empty but the dom remains
 */
export const insertSignature = (
    content = '',
    signature = '',
    mailSettings: MailSettings,
    userSettings: Partial<UserSettings>,
    fontStyle: string | undefined
) => {
    const template = templateBuilder(signature, mailSettings, userSettings, fontStyle);

    // Parse the current message and append before it the signature
    const element = parseStringToDOM(content);
    element.body.insertAdjacentHTML('afterbegin', template);

    return element.body.innerHTML;
};

/**
 * Return the content of the message with the signature switched from the old one to the new one
 */
export const changeSignature = (
    message: MessageState,
    mailSettings: Partial<MailSettings> | undefined,
    userSettings: Partial<UserSettings> | undefined,
    fontStyle: string | undefined,
    oldSignature: string,
    newSignature: string
) => {
    if (isPlainText(message.data)) {
        const oldTemplate = templateBuilder(oldSignature, mailSettings, userSettings, fontStyle, true);
        const newTemplate = templateBuilder(newSignature, mailSettings, userSettings, fontStyle, true);
        const content = getPlainTextContent(message);
        const oldSignatureText = exportPlainTextSignature(oldTemplate).trim();
        const newSignatureText = exportPlainTextSignature(newTemplate).trim();

        // Special case when there was no signature before
        if (oldSignatureText === '') {
            return `${content}\n\n${newSignatureText}`;
        }

        return (
            content
                .replace(oldSignatureText, newSignatureText)
                // Remove empty lines at the end, remove all lines if no signatures
                .trimEnd()
        );
    }
    const document = message.messageDocument?.document as Element | undefined;

    if (!document) {
        return '';
    }

    const userSignature = [...document.querySelectorAll(`.${CLASSNAME_SIGNATURE_USER}`)].find(
        (element) => element.closest(`.${CLASSNAME_BLOCKQUOTE}`) === null
    );

    if (userSignature) {
        const protonSignature = getProtonSignature(mailSettings, userSettings);
        const { userClass, containerClass } = getClassNamesSignature(newSignature, protonSignature);

        userSignature.innerHTML = replaceLineBreaks(newSignature);
        userSignature.className = `${CLASSNAME_SIGNATURE_USER} ${userClass}`;

        const signatureContainer = userSignature?.closest(`.${CLASSNAME_SIGNATURE_CONTAINER}`);
        if (signatureContainer && signatureContainer !== null) {
            signatureContainer.className = `${CLASSNAME_SIGNATURE_CONTAINER} ${containerClass}`;
        }
    }

    return document.innerHTML;
};
